import { Component, OnInit, Input } from '@angular/core';
import { UpdateComponent } from '@app/shared/components/base/update.component';
import * as _ from 'lodash-es';
import { PatchValue } from '@app/shared/components/base/base-patch.component';
import { TranslationEntry } from '@app/shared/models/translatable';
import { ScreenSize, ScreenSizeService } from '@app/core/services/screen-size.service';

export interface DynamicCheckBoxOption {
  key: string;
  selected: boolean;
  display: boolean;
  disabled?: boolean;
  trans?: string;
}

export interface AmountPerRow {
    amount:  number;
    col: string;
}

export interface AmountPerRowScreenSizeConfig {
    xs: AmountPerRow;
    sm: AmountPerRow;
    md: AmountPerRow;
    lg: AmountPerRow;
    xl: AmountPerRow;
}

@Component({
  selector: 'dynamic-checkboxes',
  templateUrl: './dynamic-checkboxes.component.html',
  styleUrls: ['./dynamic-checkboxes.component.scss']
})
export class DynamicCheckboxesComponent extends UpdateComponent implements OnInit {

  @Input()
  private current: string[];

  @Input()
  private options: TranslationEntry[];

  @Input()
  private disabledOptions: string[];

  @Input()
  public title?: string;

  @Input()
  private patchPath?: string;

  @Input()
  private checkboxAmountPerRowForScreenSize: AmountPerRowScreenSizeConfig;

  private defaultCheckboxAmountPerRowForScreenSize: AmountPerRowScreenSizeConfig = {
      xs: { amount: 1, col: 'col-12' },
      sm: { amount: 2, col: 'col-6' },
      md: { amount: 2, col: 'col-6' },
      lg: { amount: 3, col: 'col-4' },
      xl: { amount: 4, col: 'col-3' }
  };

  public selections: DynamicCheckBoxOption[];

  public checkBoxRows: DynamicCheckBoxOption[][];
  public activeCheckboxRowConfig: AmountPerRow;

  constructor(protected screenSizeService: ScreenSizeService) {
    super();
  }

  public ngOnInit() {
    this.checkboxAmountPerRowForScreenSize = this.checkboxAmountPerRowForScreenSize || this.defaultCheckboxAmountPerRowForScreenSize;
    this.setCurrentSelection();
    this.subscriptions.push(this.screenSizeService.bootstrapScreenSizeObservable
                          .subscribe((size: ScreenSize) => {
      this.groupSelectionsForScreenSize(size);
    }));
  }

  private groupSelectionsForScreenSize(size: ScreenSize): void {
    if (size) {
      this.activeCheckboxRowConfig = this.checkboxAmountPerRowForScreenSize[size] || this.defaultCheckboxAmountPerRowForScreenSize[size];

      const visibleSelections = this.selections.filter(selection => selection.display);
      const selectionsLength = visibleSelections.length;

      this.checkBoxRows = [];

      for (let i = 0; i < selectionsLength; i += this.activeCheckboxRowConfig.amount) {
        const daSlice = visibleSelections.slice(i, i + this.activeCheckboxRowConfig.amount);
        this.checkBoxRows.push(daSlice);
      }
    }
  }

  private setCurrentSelection(): void {
    if (this.current && this.options) {
      this.selections = [];

      _.forEach(this.options, (option) => {
        const disabled = this.disabledOptions && _.find(this.disabledOptions, (disabledOption) => disabledOption === option.key);

        this.selections.push({
          key: option.key,
          trans: option.trans,
          selected: this.current.indexOf(option.key) > -1,
          display: true,
          disabled: !!disabled
        });
      });

      _.forEach(this.current, (option) => {
        const exists = _.find(this.selections, (selection) => selection.key === option);
        if (!exists) {
          this.selections.unshift({
            key: option,
            selected: true,
            display: false
          });
        }
      });
    }
  }

  private selectionToStringArray(): string[] {
    const stringArray = this.selections.filter(selection => selection.selected)
                                       .map(selection => selection.key);
    return stringArray;
  }

  private updateSelection(updatedValue: PatchValue): void {
    const updated = _.find(this.selections, (selection) => selection.key === updatedValue.propertyName);
    updated.selected = updatedValue.value;
  }

  public toggleSelect(updatedValue: PatchValue): void {
    this.updateSelection(updatedValue);
    this.onChange(this.patchPath, this.selectionToStringArray());
  }

}
